/**
 * ============================================================================
 * TenantConfigAdmin — Phase 3 frontend
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: tenant-admin screen to view + edit tenant configuration keys.
 *          Three sensitivity tiers: PUBLIC (single-sign), PRIVATE (redacted
 *          to "***" in snapshot), GMP_CRITICAL (dual-sign required).
 *
 * UX RULES:
 *   - PRIVATE values in the snapshot display as "***".
 *   - GMP_CRITICAL writes route through TWO SignatureConfirmationModal
 *     invocations (one for TENANT_CONFIG_DUAL_SIGN_A, one for
 *     TENANT_CONFIG_DUAL_SIGN_B). The two signers MUST be different users.
 *   - The sensitivity tier is locked once set — downgrade attempts fail
 *     server-side with 422.
 *
 * URS rows: TCFG-001..010, SIG-011 (dual-sign meanings), SEC-027.
 * ============================================================================
 */

import { useMemo, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero, SignatureConfirmationModal } from '@edms/design-system';
import {
  useTenantConfigSnapshot,
  useRequestSignatureChallenge,
  useCurrentUser,
  type TenantConfigEntry,
} from '../../api/hooks';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';
import tcstyles from './TenantConfigAdmin.module.css';
import { AiSettingsPanel } from './AiSettingsPanel';

const SENSITIVITY_OPTIONS = ['PUBLIC', 'PRIVATE', 'GMP_CRITICAL'] as const;

export function TenantConfigAdmin() {
  const { t } = useTranslation();
  const snapshotQ = useTenantConfigSnapshot();
  const [filter, setFilter] = useState<string>('');
  const [editKey, setEditKey] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const all: TenantConfigEntry[] = Object.entries(snapshotQ.data ?? {}).map(([, v]) => ({
      ...v,
    }));
    if (!filter) return all;
    const f = filter.toLowerCase();
    return all.filter((e) => e.key.toLowerCase().includes(f));
  }, [snapshotQ.data, filter]);

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('tenantConfigAdmin.eyebrow', { defaultValue: 'Admin' })}
        title={t('tenantConfigAdmin.title', {
          defaultValue: 'Tenant configuration',
        })}
        subtitle={t('tenantConfigAdmin.subtitle', {
          defaultValue:
            'PRIVATE values are redacted to *** in this view. GMP_CRITICAL keys require two distinct signers.',
        })}
        actions={
          <Button variant="primary" leadingIcon="+" onClick={() => setEditKey('')}>
            {t('tenantConfigAdmin.actions.new', { defaultValue: 'New key' })}
          </Button>
        }
      />

      {editKey !== null && (
        <ConfigEditDialog
          initialKey={editKey || ''}
          initialEntry={
            editKey ? snapshotQ.data?.[editKey] : undefined
          }
          onClose={() => setEditKey(null)}
          onSaved={() => {
            setEditKey(null);
            snapshotQ.refetch();
          }}
        />
      )}

      {/* AI-000-G — AI cost-governor panel */}
      <AiSettingsPanel />

      <Card>
        <CardHeader>
          <div className={tcstyles.filterRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('tenantConfigAdmin.entries.title', { defaultValue: 'Configuration keys' })}{' '}
              <span style={{ color: 'var(--color-text-muted)' }}>({filtered.length})</span>
            </h2>
            <input
              type="search"
              className={tcstyles.searchInput}
              placeholder={t('tenantConfigAdmin.search.placeholder', { defaultValue: 'Filter keys…' })}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label={t('tenantConfigAdmin.search.label', { defaultValue: 'Search configuration keys' })}
            />
          </div>
        </CardHeader>
        <CardBody>
          {snapshotQ.isLoading && <p role="status">Loading…</p>}
          <table className={wstyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('tenantConfigAdmin.columns.key', { defaultValue: 'Key' })}</th>
                <th scope="col">{t('tenantConfigAdmin.columns.value', { defaultValue: 'Value' })}</th>
                <th scope="col">{t('tenantConfigAdmin.columns.sensitivity', { defaultValue: 'Sensitivity' })}</th>
                <th scope="col">{t('tenantConfigAdmin.columns.version', { defaultValue: 'Version' })}</th>
                <th scope="col">{t('tenantConfigAdmin.columns.actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.key}>
                  <td><code>{e.key}</code></td>
                  <td>{e.sensitivity === 'PRIVATE' ? <em>***</em> : e.value}</td>
                  <td>
                    <span className={`${tcstyles.tierPill} ${tcstyles[`tier-${e.sensitivity}`]}`}>
                      {e.sensitivity}
                    </span>
                  </td>
                  <td>v{e.version}</td>
                  <td>
                    <Button size="sm" variant="tertiary" onClick={() => setEditKey(e.key)}>
                      {t('common.edit')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

interface ConfigEditDialogProps {
  initialKey: string;
  initialEntry?: { value: string; sensitivity: string; version: number };
  onClose: () => void;
  onSaved: () => void;
}

function ConfigEditDialog({ initialKey, initialEntry, onClose, onSaved }: ConfigEditDialogProps) {
  const { t } = useTranslation();
  const [key, setKey] = useState(initialKey);
  const [value, setValue] = useState(initialEntry?.value ?? '');
  const [sensitivity, setSensitivity] = useState<string>(initialEntry?.sensitivity ?? 'PUBLIC');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // F1-4 — GMP_CRITICAL dual-sign via the real signature ceremony (replaces the
  // old window.prompt for opaque "bundle ids"). Two sequential signers confirm
  // identity against the server-issued attestation; their challenge tokens are
  // the bundle references. (True distinct-user SoD enforcement is server-side,
  // tracked as F2-4.)
  const [sigStep, setSigStep] = useState<'A' | 'B' | null>(null);
  const [sigA, setSigA] = useState<string | null>(null);
  const challengeM = useRequestSignatureChallenge();
  const meQ = useCurrentUser();

  const isNew = !initialKey;
  const isCritical = sensitivity === 'GMP_CRITICAL';

  async function doPut(signatureBundleIds: string[], reauthPassword?: string) {
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { value, sensitivity };
      if (signatureBundleIds.length) body['signature_bundle_ids'] = signatureBundleIds;
      // B-5 — real Part-11 re-auth: the signer's password is verified server-side.
      if (reauthPassword) body['reauth_password'] = reauthPassword;
      const res = await fetch(apiUrl('/api/v1/tenant-config/') + encodeURIComponent(key), {
        method: 'PUT',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify(body),
      });
      if (res.status >= 400) {
        const problem = await res.json().catch(() => ({}));
        setError(problem.detail || problem.title || 'Server rejected the change.');
      } else {
        onSaved();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
      setSigStep(null);
      setSigA(null);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!key.trim()) {
      setError(t('tenantConfigAdmin.errors.keyRequired', { defaultValue: 'Key is required.' }));
      return;
    }
    if (!value.trim()) {
      setError(t('tenantConfigAdmin.errors.valueRequired', { defaultValue: 'Value is required.' }));
      return;
    }
    if (!isCritical) {
      void doPut([]);
      return;
    }
    // GMP_CRITICAL: open the first signature ceremony (signer A).
    setSubmitting(true);
    challengeM.mutate(
      {
        target_type: 'tenant_config',
        target_id: key.trim() || 'new',
        signature_meaning: 'TENANT_CONFIG_DUAL_SIGN_A',
      },
      {
        onSuccess: () => setSigStep('A'),
        onError: (err: unknown) => {
          setSubmitting(false);
          setError(err instanceof Error ? err.message : 'Could not start the signature ceremony.');
        },
      },
    );
  }

  // Signer A confirms → capture token A, request the counter-signature challenge.
  function onConfirmA() {
    const tokenA = challengeM.data?.challenge_token ?? null;
    setSigA(tokenA);
    setSigStep(null);
    challengeM.mutate(
      {
        target_type: 'tenant_config',
        target_id: key.trim() || 'new',
        signature_meaning: 'TENANT_CONFIG_DUAL_SIGN_B',
      },
      {
        onSuccess: () => setSigStep('B'),
        onError: (err: unknown) => {
          setSubmitting(false);
          setError(err instanceof Error ? err.message : 'Could not start the counter-signature.');
        },
      },
    );
  }

  // Signer B confirms (with password) → re-auth + submit both bundle references.
  function onConfirmB(_initials: string, password?: string) {
    const tokenB = challengeM.data?.challenge_token ?? null;
    setSigStep(null);
    if (!sigA || !tokenB) {
      setSubmitting(false);
      setError(t('tenantConfigAdmin.errors.dualSignRequired', {
        defaultValue: 'Two distinct signatures are required for GMP_CRITICAL keys.',
      }));
      return;
    }
    void doPut([sigA, tokenB], password);
  }

  function cancelSigning() {
    setSigStep(null);
    setSigA(null);
    setSubmitting(false);
  }

  return (
    <div className={styles.backdrop} role="presentation" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="tc-edit-title">
        <header className={styles.header}>
          <h2 id="tc-edit-title" className={styles.title}>
            {isNew
              ? t('tenantConfigAdmin.edit.newTitle', { defaultValue: 'New configuration key' })
              : t('tenantConfigAdmin.edit.editTitle', { defaultValue: 'Edit configuration key' })}
          </h2>
          {isCritical && (
            <p className={styles.desc}>
              {t('tenantConfigAdmin.edit.gmpWarning', {
                defaultValue: 'GMP_CRITICAL: this change requires two distinct signers (TENANT_CONFIG_DUAL_SIGN_A and _B).',
              })}
            </p>
          )}
        </header>
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {error && <div role="alert" className={styles.errorBanner}>{error}</div>}
          <div className={styles.field}>
            <label htmlFor="tc-key" className={styles.label}>
              {t('tenantConfigAdmin.fields.key', { defaultValue: 'Key' })}
              <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="tc-key"
              className={styles.input}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={!isNew}
              placeholder="branding.logo_url"
              required
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="tc-value" className={styles.label}>
              {t('tenantConfigAdmin.fields.value', { defaultValue: 'Value' })}
              <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="tc-value"
              className={styles.input}
              rows={3}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="tc-sensitivity" className={styles.label}>
              {t('tenantConfigAdmin.fields.sensitivity', { defaultValue: 'Sensitivity' })}
            </label>
            <select
              id="tc-sensitivity"
              className={styles.select}
              value={sensitivity}
              onChange={(e) => setSensitivity(e.target.value)}
              disabled={!isNew}
            >
              {SENSITIVITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <footer className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" isLoading={submitting}>
              {t('tenantConfigAdmin.actions.save', { defaultValue: 'Save' })}
            </Button>
          </footer>
        </form>
      </div>

      {/* F1-4 — real dual-signature ceremony for GMP_CRITICAL keys (was window.prompt). */}
      {sigStep && challengeM.data && (
        <SignatureConfirmationModal
          key={sigStep}
          isOpen
          signerFullName={meQ.data?.full_name ?? ''}
          signerJobTitle={meQ.data?.role ?? ''}
          expectedInitials={meQ.data?.initials ?? ''}
          requireInitials
          requirePassword
          signatureMeaning="QA_APPROVAL"
          target={
            sigStep === 'A'
              ? t('tenantConfigAdmin.dualSign.targetA', {
                  defaultValue: 'Config "{{k}}" — primary signature (TENANT_CONFIG_DUAL_SIGN_A)',
                  k: key,
                })
              : t('tenantConfigAdmin.dualSign.targetB', {
                  defaultValue:
                    'Config "{{k}}" — counter-signature by a second, distinct authorised QA signer (TENANT_CONFIG_DUAL_SIGN_B)',
                  k: key,
                })
          }
          utcTimestamp={challengeM.data.challenge_expires_at}
          attestationText={challengeM.data.attestation_text}
          attestationTextVersion={challengeM.data.attestation_version}
          onCancel={cancelSigning}
          onConfirmIdentity={sigStep === 'A' ? onConfirmA : onConfirmB}
        />
      )}
    </div>
  );
}
